import { useMemo, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import type { WorkspaceArtifactRow } from './ExistingArtifactsPicker.types';

interface UsePickerSelectionParams {
  rows: WorkspaceArtifactRow[];
  selectedIds: Set<string>;
  attachedIds: Set<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
}

interface PickerSelection {
  selectableIdCount: number;
  allSelectableSelected: boolean;
  someSelectableSelected: boolean;
  /** Click handler the table forwards per row; supports shift-click range select. */
  onRowActivate: (index: number, event: ReactMouseEvent) => void;
  onToggleSelectAll: () => void;
}

/**
 * Multiselect + shift-click range logic shared by every picker built on
 * `PickerTable`. The host owns the `selectedIds` set (fully controlled); this
 * hook only computes the next set and hands it back through
 * `onSelectedIdsChange`.
 *
 * The range anchor lives in refs that drop when the table subtree remounts
 * (hosts key the table on search/filter/sort), so a list reshape clears the
 * anchor without an effect.
 */
export function usePickerSelection(params: UsePickerSelectionParams): PickerSelection {
  const { rows, selectedIds, attachedIds, onSelectedIdsChange } = params;

  // Anchor for shift-click range select. Tracks the last row toggled in plain
  // mode + the resulting intent (add vs remove) so a subsequent shift-click
  // extends the same intent across the range. Mirrors macOS Finder / Gmail.
  const lastSelectedIndexRef = useRef<number | null>(null);
  const lastSelectedModeRef = useRef<'add' | 'remove'>('add');

  const onRowActivate = (index: number, event: ReactMouseEvent) => {
    const row = rows[index];
    if (attachedIds.has(row.id)) return;

    if (event.shiftKey && lastSelectedIndexRef.current !== null) {
      const mode = lastSelectedModeRef.current;
      const from = Math.min(lastSelectedIndexRef.current, index);
      const to = Math.max(lastSelectedIndexRef.current, index);
      // Same-content guard: skip the state update (and the cascade of memos /
      // row re-renders) when every selectable row in [from, to] is already in
      // the requested mode.
      const next = new Set(selectedIds);
      let changed = false;
      for (let i = from; i <= to; i++) {
        const r = rows[i];
        if (attachedIds.has(r.id)) continue;
        if (mode === 'add' && !next.has(r.id)) {
          next.add(r.id);
          changed = true;
        } else if (mode === 'remove' && next.has(r.id)) {
          next.delete(r.id);
          changed = true;
        }
      }
      if (changed) onSelectedIdsChange(next);
      return;
    }

    const willSelect = !selectedIds.has(row.id);
    const next = new Set(selectedIds);
    if (willSelect) next.add(row.id);
    else next.delete(row.id);
    onSelectedIdsChange(next);
    lastSelectedIndexRef.current = index;
    lastSelectedModeRef.current = willSelect ? 'add' : 'remove';
  };

  const selectableIds = useMemo(
    () => rows.map((row) => row.id).filter((id) => !attachedIds.has(id)),
    [rows, attachedIds],
  );

  const selectedSelectableCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  const allSelectableSelected =
    selectableIds.length > 0 && selectedSelectableCount === selectableIds.length;
  const someSelectableSelected =
    selectedSelectableCount > 0 && selectedSelectableCount < selectableIds.length;

  const onToggleSelectAll = () => {
    const next = new Set(selectedIds);
    let changed = false;
    if (allSelectableSelected) {
      for (const id of selectableIds) {
        if (next.delete(id)) changed = true;
      }
    } else {
      for (const id of selectableIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
    }
    if (changed) onSelectedIdsChange(next);
  };

  return {
    selectableIdCount: selectableIds.length,
    allSelectableSelected,
    someSelectableSelected,
    onRowActivate,
    onToggleSelectAll,
  };
}
